import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CircleDashed, Play, RotateCcw, Shuffle } from 'lucide-react';
import StartButton from '@/components/site/StartButton';
import SectionHeader from '@/components/site/SectionHeader';
import GoldText from '@/components/site/GoldText';
import { cn } from '@/lib/utils';

type StepTone = 'done' | 'flag' | 'stop';

interface Scenario {
  id: string;
  label: string;
  from: string;
  request: string;
  steps: { action: string; detail: string; tone: StepTone }[];
  result: {
    kind: string;
    title: string;
    body: string[];
    fields: [string, string][];
    status: string;
  };
}

// Guided simulation: every step and output below is prewritten sample content
// for a fictional business. It is labelled as such in the UI.
const scenarios: Scenario[] = [
  {
    id: 'service-question',
    label: 'Straightforward question',
    from: 'Website form · Jordan P.',
    request: 'Hi — do you service commercial HVAC systems, and how soon could someone come out for a quote?',
    steps: [
      { action: 'Organize details', detail: 'Commercial HVAC · quote request · timing question', tone: 'done' },
      { action: 'Retrieve approved information', detail: 'Services list and scheduling policy matched', tone: 'done' },
      { action: 'Check for missing information', detail: 'Nothing required is missing', tone: 'done' },
      { action: 'Prepare output', detail: 'Reply drafted for review', tone: 'done' },
    ],
    result: {
      kind: 'Response draft',
      title: 'Re: Commercial HVAC quote',
      body: [
        'Hi Jordan, thanks for getting in touch. Yes — we service commercial HVAC systems.',
        'Quote visits are typically scheduled within the week. Could you share the site address and a couple of times that work for you?',
      ],
      fields: [
        ['Category', 'Commercial service · quote'],
        ['Sources used', 'Services list, scheduling policy'],
        ['Send', 'Requires approval'],
      ],
      status: 'Ready for human review',
    },
  },
  {
    id: 'missing-details',
    label: 'Missing details',
    from: 'Email · info@ inbox',
    request: 'Can you send me pricing? We need it for next week.',
    steps: [
      { action: 'Organize details', detail: 'Pricing request · deadline next week', tone: 'done' },
      { action: 'Retrieve approved information', detail: 'Pricing depends on service type and size', tone: 'done' },
      {
        action: 'Check for missing information',
        detail: 'Service type, location and business name not provided',
        tone: 'flag',
      },
      { action: 'Prepare output', detail: 'Clarifying reply drafted', tone: 'done' },
    ],
    result: {
      kind: 'Structured record + draft',
      title: 'Pricing request — incomplete',
      body: [
        'Thanks for reaching out. To send accurate pricing, could you tell us which service you need, the location, and the size of the job?',
        'Once we have that, we’ll get a quote back to you before your deadline.',
      ],
      fields: [
        ['Deadline', 'Next week'],
        ['Missing', 'Service type · location · business name'],
        ['Send', 'Requires approval'],
      ],
      status: 'Ready for human review',
    },
  },
  {
    id: 'escalation',
    label: 'Should escalate',
    from: 'Email · existing customer',
    request: 'Your technician damaged our ceiling yesterday. I want a refund and I’m considering legal action.',
    steps: [
      { action: 'Organize details', detail: 'Existing customer · damage complaint · refund demand', tone: 'done' },
      {
        action: 'Retrieve approved information',
        detail: 'Complaints and claims are outside this assistant’s scope',
        tone: 'stop',
      },
      { action: 'Check for missing information', detail: 'Not attempted — request is out of scope', tone: 'stop' },
      { action: 'Prepare output', detail: 'Escalated to a person; no reply drafted', tone: 'flag' },
    ],
    result: {
      kind: 'Escalation',
      title: 'Handed to: Operations manager',
      body: [
        'This request involves a damage claim, a refund and possible legal action. The assistant is not permitted to respond or make commitments on these topics.',
        'It has summarized the message and routed it to the named owner for a personal reply.',
      ],
      fields: [
        ['Reason', 'Complaint / legal — out of scope'],
        ['Priority', 'High'],
        ['Draft reply', 'None — by design'],
      ],
      status: 'Escalated to a person',
    },
  },
];

const STEP_MS = 650;

const toneIcon = (tone: StepTone) =>
  tone === 'done' ? (
    <Check className="h-3.5 w-3.5" aria-hidden="true" />
  ) : (
    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
  );

const InteractiveDemo = () => {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(0); // number of steps shown
  const [running, setRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const timers = useRef<number[]>([]);
  const outputRef = useRef<HTMLDivElement>(null);
  const scenario = scenarios[index];
  const complete = revealed === scenario.steps.length;

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const reset = (next: number) => {
    clearTimers();
    setIndex(next);
    setRevealed(0);
    setRunning(false);
    setShowOutput(false);
  };

  const run = () => {
    clearTimers();
    setShowOutput(false);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setRevealed(scenario.steps.length);
      return;
    }
    setRevealed(0);
    setRunning(true);
    scenario.steps.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(
          () => {
            setRevealed(i + 1);
            if (i === scenario.steps.length - 1) setRunning(false);
          },
          STEP_MS * (i + 1),
        ),
      );
    });
  };

  const viewOutput = () => {
    setShowOutput(true);
    requestAnimationFrame(() => outputRef.current?.focus());
  };

  const statusText = running
    ? `Running step ${Math.min(revealed + 1, scenario.steps.length)} of ${scenario.steps.length}`
    : complete
      ? `Complete — ${scenario.result.status.toLowerCase()}`
      : 'Ready to run';

  return (
    <section id="demo" aria-labelledby="demo-title" className="scroll-mt-20 border-t border-rule">
      <div className="section-container">
        <SectionHeader
          id="demo-title"
          eyebrow="02 / The demo"
          title={
            <>
              Don’t just ask what AI can do. <GoldText>Watch the workflow.</GoldText>
            </>
          }
        >
          <p>
            Explore how a sample assistant turns an incoming request into a useful next step—with clear boundaries and
            human review.
          </p>
        </SectionHeader>

        <div className="reveal mt-12 flex flex-wrap items-center gap-3">
          <span className="mr-1 border border-goldcore/40 px-2 py-1 font-mono text-[0.68rem] uppercase tracking-[0.18em]">
            <span className="gold-shine">Guided simulation</span>
          </span>
          <span className="meta-label">Customer Inquiry Assistant · scenario:</span>
          {scenarios.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={i === index}
              onClick={() => reset(i)}
              className={cn(
                'border px-3.5 py-2 font-mono text-[0.7rem] uppercase tracking-[0.12em] transition-colors',
                i === index ? 'border-goldcore text-goldcore' : 'border-rule2 text-softwhite/85 hover:border-mist/50',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="reveal mt-6 grid gap-4 lg:grid-cols-3">
          {/* 1. The request */}
          <div className="surface flex flex-col p-5">
            <p className="meta-label mb-4">1 · The request</p>
            <p className="mb-3 font-mono text-xs text-mist">{scenario.from}</p>
            <blockquote className="flex-1 border-l-2 border-goldcore/60 pl-4 font-serif text-xl leading-snug">
              “{scenario.request}”
            </blockquote>
            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" onClick={run} disabled={running} className="gc-btn gc-btn-primary gc-btn-sm">
                {complete ? (
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Play className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {complete ? 'Run again' : 'Run example'}
              </button>
              <button type="button" onClick={viewOutput} disabled={!complete} className="gc-btn gc-btn-sm">
                View output
              </button>
            </div>
          </div>

          {/* 2. The workflow */}
          <div className="surface flex flex-col p-5">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="meta-label whitespace-nowrap">2 · The workflow</p>
              <p className="font-mono text-xs text-mist" aria-live="polite">
                {statusText}
              </p>
            </div>
            <ol className="space-y-3">
              {scenario.steps.map((step, i) => {
                const shown = i < revealed;
                return (
                  <li
                    key={step.action}
                    className={cn(
                      'border p-3 motion-safe:transition-colors',
                      shown ? 'border-rule bg-knightblack/60' : 'border-dashed border-rule opacity-50',
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                          !shown && 'text-mist',
                          shown && step.tone === 'done' && 'bg-goldcore/15 text-goldcore',
                          shown && step.tone !== 'done' && 'bg-victorycoral/15 text-victorycoral',
                        )}
                      >
                        {shown ? toneIcon(step.tone) : <CircleDashed className="h-3.5 w-3.5" aria-hidden="true" />}
                      </span>
                      <span className="text-sm font-semibold">{step.action}</span>
                    </div>
                    {shown && <p className="mt-1.5 pl-[1.875rem] text-sm text-mist">{step.detail}</p>}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* 3. The result */}
          <div
            ref={outputRef}
            tabIndex={-1}
            aria-label="Result"
            className="surface flex flex-col p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldcore"
          >
            <p className="meta-label mb-4">3 · The result</p>
            {showOutput ? (
              <div className="flex flex-1 flex-col gap-4">
                <div>
                  <p className="eyebrow">{scenario.result.kind}</p>
                  <h3 className="mt-1 text-2xl leading-tight">{scenario.result.title}</h3>
                </div>
                <div className="space-y-2 border border-rule bg-knightblack/60 p-4 text-sm leading-relaxed text-softwhite/90">
                  {scenario.result.body.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                </div>
                <dl className="space-y-1.5 text-sm">
                  {scenario.result.fields.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3">
                      <dt className="text-mist">{k}</dt>
                      <dd className="text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-auto border border-goldcore/30 bg-goldcore/5 px-3 py-2 font-mono text-[0.68rem] uppercase tracking-[0.14em]">
                  <span className="text-mist">Status: </span>
                  <span className="gold-shine">{scenario.result.status}</span>
                </p>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center border border-dashed border-rule p-6 text-center text-sm text-mist">
                {complete
                  ? 'Workflow complete. Select “View output” to see the result.'
                  : 'Run the example to prepare an output.'}
              </div>
            )}
            <button
              type="button"
              onClick={() => reset((index + 1) % scenarios.length)}
              className="mt-4 inline-flex items-center gap-2 self-start font-mono text-[0.7rem] uppercase tracking-[0.12em] text-softwhite/85 hover:text-goldcore"
            >
              <Shuffle className="h-4 w-4" aria-hidden="true" />
              Try another scenario
            </button>
          </div>
        </div>

        <p className="mt-5 text-sm text-mist">
          Demonstration using sample business information. Your pilot is configured around an agreed workflow and
          approved sources.
        </p>

        <div className="reveal mt-14 flex flex-col items-start gap-5 border-y border-rule py-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-serif text-3xl leading-tight sm:text-4xl">
            Have a process like this <GoldText>in your business?</GoldText>
          </p>
          <StartButton interest="lead-follow-up" />
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;
